// P0-6 FASE 2F.1G · Persistenza locale + import/restore della review VCF.
// - Nessuna scrittura di rete.
// - Chiave localStorage: `sb_vcf_review_v1__<sha256>` del contenuto file.
// - Payload versionato (schema_version) per compatibilità futura.
// - Validazione strutturale + status ammessi prima di ogni load/import.
// - PII: nomi/cognomi/note/telefoni presenti solo perché scritti
//   dall'admin nella review. Nessun token/credenziale.

export const SCHEMA_VERSION = "sarabi_vcf_review_backup_v1";
export const STORAGE_PREFIX = "sb_vcf_review_v1__";
const VALID_STATUSES = new Set(["confirmed", "kept_name_only", "skipped"]);

// ----------------------------------------------------------------------------
// Fingerprint · SHA-256 del contenuto file (Web Crypto)
// ----------------------------------------------------------------------------
export async function sha256File(file) {
  if (!file || typeof file.arrayBuffer !== "function") {
    throw new Error("sha256File: file non valido");
  }
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function storageKey(fingerprint) {
  return STORAGE_PREFIX + fingerprint;
}

// ----------------------------------------------------------------------------
// LocalStorage IO
// ----------------------------------------------------------------------------
function _safeLS() {
  try {
    return typeof window !== "undefined" && window.localStorage
      ? window.localStorage
      : null;
  } catch {
    return null;
  }
}

export function loadFromStorage(fingerprint) {
  const ls = _safeLS();
  if (!ls || !fingerprint) return null;
  try {
    const raw = ls.getItem(storageKey(fingerprint));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.schema_version !== SCHEMA_VERSION) return null;
    const v = validateBackupShape(parsed);
    if (!v.ok) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveToStorage(fingerprint, review, vcfMeta) {
  const ls = _safeLS();
  if (!ls || !fingerprint) return false;
  try {
    const payload = {
      schema_version: SCHEMA_VERSION,
      exported_at: new Date().toISOString(),
      vcf_fingerprint: fingerprint,
      vcf_meta: vcfMeta || null,
      counts: {
        decisions: Object.keys(review.decisions || {}).length,
        drafts: Object.keys(review.drafts || {}).length,
        collisionChoices: Object.keys(review.collisionChoices || {}).length,
        conflictChoices: Object.keys(review.conflictChoices || {}).length,
      },
      decisions: review.decisions || {},
      drafts: review.drafts || {},
      collisionChoices: review.collisionChoices || {},
      conflictChoices: review.conflictChoices || {},
    };
    ls.setItem(storageKey(fingerprint), JSON.stringify(payload));
    return true;
  } catch (e) {
    console.warn("[vcfReviewPersistence] save failed:", e);
    return false;
  }
}

export function resetStorage(fingerprint) {
  const ls = _safeLS();
  if (!ls || !fingerprint) return;
  try { ls.removeItem(storageKey(fingerprint)); } catch {}
}

// ----------------------------------------------------------------------------
// Validazione strutturale del backup (locale o importato)
// ----------------------------------------------------------------------------
export function validateBackupShape(json) {
  if (!json || typeof json !== "object" || Array.isArray(json)) {
    return { ok: false, reason: "JSON non valido (root non oggetto)" };
  }
  if (json.schema_version !== SCHEMA_VERSION) {
    return {
      ok: false,
      reason: `schema_version atteso "${SCHEMA_VERSION}", trovato "${json.schema_version}"`,
    };
  }
  for (const k of ["decisions", "drafts", "collisionChoices", "conflictChoices"]) {
    const v = json[k];
    if (!v || typeof v !== "object" || Array.isArray(v)) {
      return { ok: false, reason: `campo "${k}" mancante o non oggetto` };
    }
  }
  // decisions: ogni entry deve avere status ammesso
  const bad = [];
  for (const [k, v] of Object.entries(json.decisions)) {
    if (!v || typeof v !== "object") {
      bad.push(k); break;
    }
    if (!VALID_STATUSES.has(v.status)) {
      bad.push(`${k}:${v.status || "∅"}`); break;
    }
  }
  if (bad.length) {
    return { ok: false, reason: `decisions con status invalido: ${bad.join(",")}` };
  }
  // Counts dichiarati coerenti con il payload (se presenti)
  if (json.counts && typeof json.counts === "object") {
    const check = (label, expected) => {
      const actual = Object.keys(json[label] || {}).length;
      if (typeof expected === "number" && expected !== actual) {
        return `count ${label} dichiarato=${expected} ma payload=${actual}`;
      }
      return null;
    };
    for (const label of ["decisions", "drafts", "collisionChoices",
                            "conflictChoices"]) {
      const err = check(label, json.counts[label]);
      if (err) return { ok: false, reason: err };
    }
  }
  return { ok: true };
}

// ----------------------------------------------------------------------------
// Compatibilità backup ↔ VCF attualmente caricato
// Backup legacy senza `vcf_fingerprint` → fallback su metadata (filename,
// file_size_bytes, vcard_total, contacts_with_phone). Backup con
// `vcf_fingerprint` → confronto stretto SHA-256.
// ----------------------------------------------------------------------------
export function backupMatchesCurrentVcf(backup, currentReport, currentFile,
    currentFingerprint) {
  if (!backup) return { compatible: false, reason: "backup vuoto" };
  if (!currentReport?.file) {
    return { compatible: false, reason: "analisi VCF corrente non disponibile" };
  }

  // 1. Se backup ha fingerprint → confronto stretto SHA-256 (unica prova
  //    di identità sufficiente; se non matcha ABORT anche con metadata OK).
  if (backup.vcf_fingerprint) {
    if (!currentFingerprint) {
      return {
        compatible: false,
        reason: "fingerprint SHA-256 corrente non disponibile",
      };
    }
    if (backup.vcf_fingerprint !== currentFingerprint) {
      return {
        compatible: false,
        reason: `fingerprint SHA-256 diverso · backup=${backup.vcf_fingerprint.slice(0, 12)}… · attuale=${currentFingerprint.slice(0, 12)}…`,
      };
    }
    return { compatible: true };
  }

  // 2. Backup LEGACY (senza fingerprint) · match ESATTO su:
  //    - file_size_bytes
  //    - vcard_total
  //    - contacts_with_phone
  //    Il filename è informativo (warning) ma NON bloccante: i client
  //    possono sanitizzarlo diversamente (es. spazi → underscore).
  const bm = backup.vcf_meta || {};
  const cr = currentReport.file || {};
  const cwp = currentReport?.contacts?.contacts_with_phone;
  const mismatches = [];
  const warnings = [];

  if (bm.file_size_bytes == null) {
    mismatches.push("backup senza file_size_bytes");
  } else if (cr.file_size_bytes != null
      && bm.file_size_bytes !== cr.file_size_bytes) {
    mismatches.push(`file_size backup=${bm.file_size_bytes} attuale=${cr.file_size_bytes}`);
  }

  if (bm.vcard_total == null) {
    mismatches.push("backup senza vcard_total");
  } else if (cr.vcard_total != null && bm.vcard_total !== cr.vcard_total) {
    mismatches.push(`vcard_total backup=${bm.vcard_total} attuale=${cr.vcard_total}`);
  }

  if (bm.contacts_with_phone == null) {
    mismatches.push("backup senza contacts_with_phone");
  } else if (cwp != null && bm.contacts_with_phone !== cwp) {
    mismatches.push(`contacts_with_phone backup=${bm.contacts_with_phone} attuale=${cwp}`);
  }

  // Filename: solo warning informativo (non blocca).
  if (bm.filename && currentFile?.name && bm.filename !== currentFile.name) {
    warnings.push(`filename backup="${bm.filename}" attuale="${currentFile.name}"`);
  }

  if (mismatches.length) {
    return { compatible: false, reason: mismatches.join(" · ") };
  }
  return { compatible: true, warnings: warnings.length ? warnings : null };
}

// ----------------------------------------------------------------------------
// Estrai il vcf_meta dal report backend per salvarlo assieme al backup
// (safeguard futuro contro cross-file restore accidentale)
// ----------------------------------------------------------------------------
export function extractVcfMeta(currentReport, currentFile) {
  return {
    filename: currentReport?.file?.filename || currentFile?.name || null,
    file_size_bytes: currentReport?.file?.file_size_bytes ?? null,
    vcard_total: currentReport?.file?.vcard_total ?? null,
    contacts_with_phone: currentReport?.contacts?.contacts_with_phone ?? null,
    phase: currentReport?.phase || null,
  };
}
